import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { downloadFile } from "../utils/download";
import { formatCurrency } from "../utils/currency";
import { formatQuantity } from "../utils/quantity";
import { localIsoDate } from "../utils/date";

const CHART_COLORS = ["#7c3aed", "#667eea", "#3b82f6", "#16a34a", "#f59e0b", "#ef4444", "#0ea5e9", "#a855f7"];

// One row per report in the Downloads table; each format present becomes a
// clickable cell (via handleExport(path, filename, options)), a format
// left out of a report renders as a blank cell (e.g. Profit & Loss has no
// csv/xlsx). Centralizing this as data means a future export is one array
// entry instead of several near-identical buttons.
const DOWNLOAD_REPORTS = [
  {
    label: "Sales",
    csv: { path: "sales.csv", filename: "sales.csv" },
    xlsx: { path: "sales.xlsx", filename: "sales.xlsx" },
  },
  {
    label: "Expenses",
    csv: { path: "expenses.csv", filename: "expenses.csv" },
    xlsx: { path: "expenses.xlsx", filename: "expenses.xlsx" },
  },
  {
    label: "Low Stock",
    csv: { path: "low-stock.csv", filename: "low-stock.csv", options: { includeDates: false } },
    xlsx: { path: "low-stock.xlsx", filename: "low-stock.xlsx", options: { includeDates: false } },
  },
  {
    label: "Top Products by Revenue",
    csv: { path: "top-products.csv", filename: "top-products-by-revenue.csv" },
    xlsx: { path: "top-products.xlsx", filename: "top-products-by-revenue.xlsx" },
  },
  {
    label: "Top Products by Quantity",
    csv: {
      path: "top-products.csv",
      filename: "top-products-by-quantity.csv",
      options: { extraParams: { sort_by: "quantity" } },
    },
    xlsx: {
      path: "top-products.xlsx",
      filename: "top-products-by-quantity.xlsx",
      options: { extraParams: { sort_by: "quantity" } },
    },
  },
  {
    label: "Stock Movement Summary",
    csv: { path: "stock-movements.csv", filename: "stock-movements.csv" },
    xlsx: { path: "stock-movements.xlsx", filename: "stock-movements.xlsx" },
  },
  {
    label: "Stock Movement by Product",
    csv: { path: "stock-movements-by-product.csv", filename: "stock-movements-by-product.csv" },
    xlsx: { path: "stock-movements-by-product.xlsx", filename: "stock-movements-by-product.xlsx" },
  },
  {
    label: "Profit & Loss",
    pdf: { path: "profit-loss.pdf", filename: "profit-loss.pdf" },
  },
];

export default function ReportsPage() {
  const { user, activeShop } = useAuth();
  const isOrgAdmin = activeShop?.role === "org_admin";

  const [scope, setScope] = useState("shop"); // "shop" | "org"
  const [startDate, setStartDate] = useState(localIsoDate());
  const [endDate, setEndDate] = useState(localIsoDate());
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsByQuantity, setTopProductsByQuantity] = useState([]);
  const [expensesByCategory, setExpensesByCategory] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [inventoryValuation, setInventoryValuation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");

  const basePath = useMemo(() => {
    if (scope === "org" && isOrgAdmin && user) {
      return `/organizations/${user.organization_id}/reports`;
    }
    return `/shops/${activeShop?.shop_id}/reports`;
  }, [scope, isOrgAdmin, user, activeShop]);

  const dateParams = useMemo(() => ({ start_date: startDate || undefined, end_date: endDate || undefined }), [
    startDate,
    endDate,
  ]);

  const fetchAll = useCallback(async () => {
    if (!activeShop) return;
    setLoading(true);
    setError("");
    try {
      const [summaryRes, trendRes, expensesRes, lowStockRes, inventoryValuationRes] = await Promise.all([
        api.get(`${basePath}/summary`, { params: dateParams }),
        api.get(`${basePath}/sales-trend`, { params: dateParams }),
        api.get(`${basePath}/expenses-by-category`, { params: dateParams }),
        api.get(`${basePath}/low-stock`),
        api.get(`${basePath}/inventory-valuation`),
      ]);
      setSummary(summaryRes.data);
      setTrend(trendRes.data);
      setExpensesByCategory(expensesRes.data);
      setLowStock(lowStockRes.data);
      setInventoryValuation(inventoryValuationRes.data);

      if (scope === "shop") {
        const [topByRevenueRes, topByQuantityRes] = await Promise.all([
          api.get(`${basePath}/top-products`, { params: dateParams }),
          api.get(`${basePath}/top-products`, { params: { ...dateParams, sort_by: "quantity" } }),
        ]);
        setTopProducts(topByRevenueRes.data);
        setTopProductsByQuantity(topByQuantityRes.data);
      } else {
        setTopProducts([]);
        setTopProductsByQuantity([]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load reports");
    }
    setLoading(false);
  }, [basePath, dateParams, activeShop, scope]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleExport = async (path, filename, { includeDates = true, extraParams = {} } = {}) => {
    setExporting(filename);
    setError("");
    try {
      const params = new URLSearchParams();
      if (includeDates) {
        if (startDate) params.set("start_date", startDate);
        if (endDate) params.set("end_date", endDate);
      }
      Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));
      const query = params.toString() ? `?${params.toString()}` : "";
      await downloadFile(`/shops/${activeShop.shop_id}/exports/${path}${query}`, filename);
    } catch (err) {
      setError("Failed to download export");
    }
    setExporting("");
  };

  const currency = (n) => formatCurrency(n, activeShop?.currency);

  // Shows product/quantity/revenue/profit together regardless of which
  // single metric the bar itself renders, so profit-per-item is visible
  // on both Top Products charts without adding a cluttering third bar.
  const TopProductsTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0].payload;
    return (
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 12px" }}>
        <div style={{ fontWeight: 700 }}>{row.product_name}</div>
        <div>Qty sold: {formatQuantity(row.quantity_sold, undefined)}</div>
        <div>Revenue: {currency(row.revenue)}</div>
        <div>Profit: {currency(row.profit)}</div>
      </div>
    );
  };

  if (!activeShop) {
    return <div className="card">No shop assigned to your account yet.</div>;
  }

  return (
    <>
      <div className="stats">
        {isOrgAdmin && (
          <select className="shop-switcher-inline" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="shop">This shop</option>
            <option value="org">All shops (consolidated)</option>
          </select>
        )}
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span>to</span>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {error && <div className="error-msg">{error}</div>}

      {loading ? (
        <div className="loader">Loading...</div>
      ) : (
        summary && (
          <>
            <div className="report-cards">
              <div className="report-card">
                <div className="report-card-label">Revenue</div>
                <div className="report-card-value">{currency(summary.revenue)}</div>
              </div>
              <div className="report-card">
                <div className="report-card-label">Cost of Goods Sold</div>
                <div className="report-card-value">{currency(summary.cogs)}</div>
              </div>
              <div className="report-card">
                <div className="report-card-label">Gross Profit</div>
                <div className="report-card-value">{currency(summary.gross_profit)}</div>
              </div>
              <div className="report-card">
                <div className="report-card-label">Expenses</div>
                <div className="report-card-value">{currency(summary.expenses_total)}</div>
              </div>
              <div className="report-card report-card-highlight">
                <div className="report-card-label">Net Profit</div>
                <div className="report-card-value">{currency(summary.net_profit)}</div>
              </div>
              <div className="report-card">
                <div className="report-card-label">Sales Count</div>
                <div className="report-card-value">{summary.sales_count}</div>
              </div>
              <div className="report-card">
                <div className="report-card-label">Avg Sale</div>
                <div className="report-card-value">{currency(summary.average_sale_value)}</div>
              </div>
              <div className="report-card">
                <div className="report-card-label">Cost of Stock</div>
                <div className="report-card-value">{currency(summary.cost_of_stock_added)}</div>
              </div>
              {inventoryValuation && (
                <>
                  <div className="report-card">
                    <div className="report-card-label">Inventory Value (Selling Price)</div>
                    <div className="report-card-value">{currency(inventoryValuation.value_at_selling_price)}</div>
                  </div>
                  <div className="report-card">
                    <div className="report-card-label">Inventory Value (Cost Price)</div>
                    <div className="report-card-value">{currency(inventoryValuation.value_at_cost_price)}</div>
                  </div>
                </>
              )}
            </div>
            {summary.cogs_has_unknown_cost_lines && (
              <div className="error-msg" style={{ marginTop: 12 }}>
                Some sold products have no cost price set - Cost of Goods Sold (and profit) may be understated.
              </div>
            )}
            {inventoryValuation &&
              (inventoryValuation.has_unknown_selling_price_products ||
                inventoryValuation.has_unknown_cost_price_products) && (
                <div className="error-msg" style={{ marginTop: 12 }}>
                  Some in-stock products have no selling or cost price set - inventory value may be understated.
                </div>
              )}
          </>
        )
      )}

      <div className="content-grid" style={{ marginTop: 20 }}>
        <div className="card">
          <h2>Sales Trend</h2>
          {trend.length === 0 ? (
            <div className="empty">No sales in this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => currency(value)} />
                <Line type="monotone" dataKey="revenue" stroke="#7c3aed" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card">
          <h2>Expenses by Category</h2>
          {expensesByCategory.length === 0 ? (
            <div className="empty">No expenses in this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={expensesByCategory}
                  dataKey="total"
                  nameKey="category_name"
                  outerRadius={90}
                  label={({ value }) => currency(value)}
                >
                  {expensesByCategory.map((entry, index) => (
                    <Cell key={entry.category_id} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => currency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {scope === "shop" && (
          <div className="card">
            <h2>Top Products By Revenue</h2>
            {topProducts.length === 0 ? (
              <div className="empty">No sales in this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={topProducts} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="product_name" type="category" width={90} tick={{ fontSize: 12 }} />
                  <Tooltip content={<TopProductsTooltip />} />
                  <Bar dataKey="revenue" fill="#667eea" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        {scope === "shop" && (
          <div className="card">
            <h2>Top Products by Quantity Sold</h2>
            {topProductsByQuantity.length === 0 ? (
              <div className="empty">No sales in this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={topProductsByQuantity} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="product_name" type="category" width={90} tick={{ fontSize: 12 }} />
                  <Tooltip content={<TopProductsTooltip />} />
                  <Bar dataKey="quantity_sold" fill="#16a34a" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        <div className="card">
          <h2>Low Stock</h2>
          {lowStock.length === 0 ? (
            <div className="empty">Nothing at or below its reorder threshold.</div>
          ) : (
            <div className="scroll-x">
              <table className="product-table product-table-compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Stock</th>
                    <th>Reorder At</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.map((p) => (
                    <tr key={p.id}>
                      <td className="name-cell">{p.name}</td>
                      <td>
                        <span className="qty-badge qty-low">{formatQuantity(p.quantity_on_hand, undefined)}</span>
                      </td>
                      <td>{formatQuantity(p.reorder_threshold, undefined)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <h2>Downloads</h2>
          <p className="muted">Exports are for the currently selected shop: {activeShop.shop_name}.</p>
          <div className="scroll-x">
            <table className="product-table">
              <thead>
                <tr>
                  <th>Report</th>
                  <th>CSV</th>
                  <th>Excel</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {DOWNLOAD_REPORTS.map((report) => (
                  <tr key={report.label}>
                    <td className="name-cell downloads-report-name">{report.label}</td>
                    {["csv", "xlsx", "pdf"].map((format) => {
                      const spec = report[format];
                      return (
                        <td key={format}>
                          {spec && (
                            <button
                              className="btn"
                              disabled={!!exporting}
                              onClick={() => handleExport(spec.path, spec.filename, spec.options)}
                            >
                              {exporting === spec.filename
                                ? "..."
                                : format === "xlsx"
                                ? "Excel"
                                : format.toUpperCase()}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
